import { INotificationData } from 'bg-server/src/components/api/v1/push/notification/types';

import { ENV } from 'src/config/environments';
import { httpRequest } from 'src/helpers/http';
import { INotificationSearchFilter } from 'src/interfaces';

class PushNotificationService {
    private getApiEndpoint(): string {
        const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
        const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
        return apiEndpoint ? `${apiEndpoint.url}/push/notification` : `${ENV.apiEndpoints[0].url}/push/notification`;
    }

    async loadUserNotificationsSvc(): Promise<INotificationData[]> {
        const response = await httpRequest(`${this.getApiEndpoint()}/all`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async readNotification(id: string) {
        const response = await httpRequest(`${this.getApiEndpoint()}/${id}/read`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async markNotificationAsDone(sharedNotId: string): Promise<INotificationData> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/${sharedNotId}/done`, 'POST');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async markNotificationAsUnDone(sharedNotId: string): Promise<INotificationData> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/${sharedNotId}/undone`, 'POST');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async markNotificationsAsDone(sharedNotIds: string[]): Promise<{ count: number }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/done`, 'POST', { sharedNotIds });
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async markNotificationsAsUnDone(sharedNotIds: string[]): Promise<{ count: number }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/undone`, 'POST', { sharedNotIds });
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async archiveNotification(sharedNotId: string): Promise<INotificationData> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/${sharedNotId}/archive`, 'POST');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async unArchiveNotification(sharedNotId: string): Promise<INotificationData> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/${sharedNotId}/unarchive`, 'POST');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async archiveNotifications(sharedNotIds: string[]): Promise<{ count: number }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/archive`, 'POST', { sharedNotIds });
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async unArchiveNotifications(sharedNotIds: string[]): Promise<{ count: number }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/shared/unarchive`, 'POST', { sharedNotIds });
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    // Funcion para testing
    async sendNotification(message: INotificationData) {
        const response = await httpRequest(`${this.getApiEndpoint()}/send`, 'POST', message);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async confirmReceived(notificationId: string, token: string) {
        const body = { token };
        await httpRequest(`${this.getApiEndpoint()}/${notificationId}/receive`, 'PUT', body);
    }

    // eslint-disable-next-line complexity
    public tranformNotificationSearchFilter(filter: INotificationSearchFilter) {
        const searchField: any = {
            $and: [],
        };

        if (filter.endDate) {
            searchField.$and.push({
                created: {
                    $lte: filter.endDate,
                },
            });
        }
        if (filter.startDate) {
            searchField.$and.push({
                created: {
                    $gte: filter.startDate,
                },
            });
        }

        if (filter.expedient) {
            searchField.$and.push({
                $expr: {
                    $regexMatch: {
                        input: { $toString: '$notification.custom.expedientId' },
                        regex: filter.expedient,
                        options: 'i',
                    },
                },
            });
        }

        if (filter.isArchived === true) {
            searchField.$and.push({
                'receiver.isArchived': true,
            });
        } else {
            searchField.$and.push(
                {
                    $or: [
                        { 'receiver.isArchived': false },
                        { 'receiver.isArchived': { $exists: false } },
                    ],
                },
            );
        }

        if (filter.estat && filter.estat.length > 0) {
            const booleanEstats = filter.estat.map(estat => parseInt(estat, 10) === 1);
            searchField.$and.push({
                $or: booleanEstats.map(estat => ({ 'receiver.isDone': estat })),
            });
        }

        if (filter.taskPriority && filter.taskPriority.length > 0) {
            searchField.$and.push({
                'notification.custom.taskPriority': { $in: filter.taskPriority },
            });
        }

        switch (filter.type) {
            case ('tots'):
                searchField.$and.push({
                    'notification.custom.resourceName': { $ne: null },
                });
                break;
            case ('notifications'):
                searchField.$and.push(
                    { 'notification.custom.taskType': { $in: ['citaCancelada', 'newExpedient', 'newSeguiment', 'tramitPendentAjuntament', 'tramitCompletat'] } },
                );
                break;
            case ('missatgesusuari'):
                searchField.$and.push({
                    'notification.custom.taskType': { $regex: 'newMessage', $options: 'i' },
                });
                break;
            case ('gestionpendingtramits'):
                searchField.$and.push(
                    {
                        $or: [
                            { 'notification.custom.taskType': { $regex: 'tramitacionPendingTramit', $options: 'i' } },
                        ],
                    },
                );
                break;
            case ('validationpendingtramits'):
                searchField.$and.push({
                    'notification.custom.taskType': { $regex: 'validationPendingTramit', $options: 'i' },
                });
                break;
            case ('solicituds'):
                searchField.$and.push(
                    {
                        $or: [
                            { 'notification.custom.taskType': { $regex: 'gestioPendingTramit', $options: 'i' } },
                            { 'notification.custom.taskType': { $regex: 'newSolicitud', $options: 'i' } },
                        ],
                    },
                );
                break;
            default:
                return false;
        }
        return searchField;
    }

    public async getNotificationsByFilter(filter: INotificationSearchFilter, page: number, pageSize = 20): Promise<{ count: number; data: INotificationData[] }> {
        const sFilter = this.tranformNotificationSearchFilter(filter);
        const hasSearchFilter = Boolean(sFilter);

        const filterParams = hasSearchFilter
            ? `search=${encodeURIComponent(JSON.stringify(sFilter))}&`
            : '';

        let orderBy = '';
        if (filter.dateOrderBy || filter.prioritatOrderBy) {
            orderBy = `sort=${filter.prioritatOrderBy ? filter.prioritatOrderBy : ''} ${filter.dateOrderBy ? filter.dateOrderBy : ''} &`;
        }
        const pageParams = page ? `page=${page}&` : '';
        const pageSizeParams = pageSize ? `pageitems=${pageSize}` : '';

        const response = await httpRequest(`${this.getApiEndpoint()}?${orderBy}${filterParams}${pageParams}${pageSizeParams}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async getNotificationsByExpedient(idExpedient: string, page: number, pageSize = 20): Promise<{ count: number; data: INotificationData[] }> {
        const pageParams = page ? `page=${page}&` : '';
        const pageSizeParams = pageSize ? `pageitems=${pageSize}` : '';

        const response = await httpRequest(`${this.getApiEndpoint()}/by-expedient/${idExpedient}?${pageParams}${pageSizeParams}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }
}

export const pushNotificationService = new PushNotificationService();
